import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import UserAvatar, { UserAvatarGroup } from '../components/UserAvatar';
import EOSCoachWidget from '../components/EOSCoachWidget';
import GMInsightsPanel from '../components/GMInsightsPanel';
import { 
  BarChart3, TrendingUp, Target, AlertTriangle, CheckCircle2, 
  Clock, Users, Zap, ListChecks, Eye, Calendar, RefreshCw,
  ArrowUp, ArrowDown, Minus, Crown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSLeadershipDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard' | 'gm-insights' | 'my-coach'
  const [showGMPanel, setShowGMPanel] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchMeetings();
  }, [selectedTeam]);

  const fetchDashboardData = async () => {
    try {
      const teamFilter = selectedTeam !== 'all' ? `?team_ids=${selectedTeam}` : '';
      const response = await axios.get(`${API}/eos/leadership/dashboard${teamFilter}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load leadership dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/eos/leadership/meetings?limit=20`);
      setMeetings(response.data);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboardData(), fetchMeetings()]);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };
  
  const handleJoinAsObserver = async (meetingId) => {
    try {
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      
      if (currentUser?.user_id) {
        await axios.post(`${API}/eos/meetings/${meetingId}/add-observer?user_id=${currentUser.user_id}`);
      }
      
      navigate(`/eos/l10-meetings/${meetingId}`);
    } catch (error) {
      console.error('Error joining as observer:', error);
      navigate(`/eos/l10-meetings/${meetingId}`);
    }
  };

  const getHealthColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const getHealthBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-100 dark:bg-green-900/30';
    if (percentage >= 60) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Leadership Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const data = dashboardData || {};
  const summary = data.summary || {};
  const meetingsStats = data.meetings || {};
  const rocksStats = data.rocks || {};
  const scorecardStats = data.scorecard || {};
  const issuesStats = data.issues || {};
  const todosStats = data.todos || {};
  const teams = data.teams || [];

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-purple-600" />
            <div>
              <h1 className="text-3xl font-bold">Leadership Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Strategic overview across all teams and operations
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* GM / AI Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
              <Crown className="h-4 w-4 text-purple-600" />
              <Label htmlFor="gm-toggle" className="text-sm font-medium cursor-pointer">
                GM Insights
              </Label>
              <Switch 
                id="gm-toggle" 
                checked={showGMPanel} 
                onCheckedChange={setShowGMPanel}
              />
            </div>
            
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(team => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* GM Insights Panel - Expandable */}
        {showGMPanel && (
          <GMInsightsPanel className="animate-in fade-in slide-in-from-top-2 duration-300" />
        )}

        {/* My EOS Coach - Compact Widget */}
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <EOSCoachWidget compact={false} />
          </div>
          <Card className="col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-600" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                size="sm"
                onClick={() => navigate('/eos/l10-meetings')}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule L10 Meeting
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                size="sm"
                onClick={() => navigate('/eos/rocks')}
              >
                <Target className="h-4 w-4 mr-2" />
                Review Rocks
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                size="sm"
                onClick={() => navigate('/eos/issues')}
              >
                <Zap className="h-4 w-4 mr-2" />
                View Issues
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                size="sm"
                onClick={() => navigate('/eos/scorecard')}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Check Scorecard
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Users className="h-8 w-8 text-blue-500" />
                <div className="text-right">
                  <div className="text-2xl font-bold">{summary.total_teams}</div>
                  <div className="text-xs text-muted-foreground">Teams</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Calendar className="h-8 w-8 text-green-500" />
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getHealthColor(meetingsStats.l10_completion_rate)}`}>
                    {meetingsStats.l10_completion_rate}%
                  </div>
                  <div className="text-xs text-muted-foreground">L10 Completion</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Target className="h-8 w-8 text-purple-500" />
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getHealthColor(rocksStats.on_track_pct)}`}>
                    {rocksStats.on_track_pct}%
                  </div>
                  <div className="text-xs text-muted-foreground">Rocks On Track</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingUp className="h-8 w-8 text-green-500" />
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getHealthColor(scorecardStats.health_pct)}`}>
                    {scorecardStats.health_pct}%
                  </div>
                  <div className="text-xs text-muted-foreground">Scorecard Health</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Zap className="h-8 w-8 text-orange-500" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-600">{issuesStats.total_open}</div>
                  <div className="text-xs text-muted-foreground">Open Issues</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ListChecks className="h-8 w-8 text-cyan-500" />
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getHealthColor(todosStats.completion_rate)}`}>
                    {todosStats.completion_rate}%
                  </div>
                  <div className="text-xs text-muted-foreground">To-Do Completion</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Meetings & Issues */}
          <div className="space-y-6">
            {/* Active Meetings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  Active Meetings
                </CardTitle>
                <CardDescription>Join meetings as an observer</CardDescription>
              </CardHeader>
              <CardContent>
                {meetings.filter(m => m.status === 'in_progress').length > 0 ? (
                  <div className="space-y-3">
                    {meetings.filter(m => m.status === 'in_progress').map(meeting => (
                      <div key={meeting.id} className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950/30">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{meeting.title}</div>
                          <Badge className="bg-blue-600">Live</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          {meeting.facilitator_display && (
                            <UserAvatar user={meeting.facilitator_display} size="xs" showName />
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleJoinAsObserver(meeting.id)}>
                            <Eye className="h-4 w-4 mr-1" />
                            Observe
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    No meetings in progress
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* High Priority Issues */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  High Priority Issues
                </CardTitle>
                <CardDescription>{issuesStats.high_priority} issues need attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(issuesStats.by_team || {}).map(([team, count]) => (
                    <div key={team} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <span className="font-medium">{team}</span>
                      <Badge variant={count > 3 ? 'destructive' : 'outline'}>{count}</Badge>
                    </div>
                  ))}
                  
                  {Object.keys(issuesStats.by_team || {}).length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      No open issues
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Middle Column - Rocks & Scorecard */}
          <div className="space-y-6">
            {/* Rocks Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  Rocks by Team
                </CardTitle>
                <CardDescription>
                  {rocksStats.total_active} active rocks across teams
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(rocksStats.by_team || {}).map(([team, stats]) => (
                    <div key={team} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{team}</span>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-green-600">{stats.on_track} on track</span>
                          <span className="text-red-600">{stats.at_risk} at risk</span>
                        </div>
                      </div>
                      <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-muted">
                        <div 
                          className="bg-green-500" 
                          style={{ width: `${(stats.on_track / stats.total) * 100}%` }}
                        />
                        <div 
                          className="bg-red-500" 
                          style={{ width: `${(stats.at_risk / stats.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {Object.keys(rocksStats.by_team || {}).length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      No rocks data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Scorecard Health */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Scorecard Health
                </CardTitle>
                <CardDescription>{scorecardStats.total_metrics} metrics tracked</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-green-500" />
                      <span>Green</span>
                    </div>
                    <span className="font-bold">{scorecardStats.green}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-yellow-500" />
                      <span>Yellow</span>
                    </div>
                    <span className="font-bold">{scorecardStats.yellow}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-500" />
                      <span>Red</span>
                    </div>
                    <span className="font-bold">{scorecardStats.red}</span>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground">Overall Health</span>
                      <span className={`font-bold ${getHealthColor(scorecardStats.health_pct)}`}>
                        {scorecardStats.health_pct}%
                      </span>
                    </div>
                    <Progress value={scorecardStats.health_pct} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Meetings Stats & To-Dos */}
          <div className="space-y-6">
            {/* Meeting Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Meeting Stats (30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${getHealthBgColor(meetingsStats.l10_completion_rate)}`}>
                      <div className="text-sm text-muted-foreground">Completion Rate</div>
                      <div className={`text-2xl font-bold ${getHealthColor(meetingsStats.l10_completion_rate)}`}>
                        {meetingsStats.l10_completion_rate}%
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${meetingsStats.meetings_over_time_pct > 20 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                      <div className="text-sm text-muted-foreground">Over Time</div>
                      <div className={`text-2xl font-bold ${meetingsStats.meetings_over_time_pct > 20 ? 'text-red-600' : 'text-green-600'}`}>
                        {meetingsStats.meetings_over_time_pct}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <div className="text-center p-2 bg-muted rounded-lg">
                      <div className="text-xl font-bold">{meetingsStats.completed_last_30_days}</div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                    </div>
                    <div className="text-center p-2 bg-muted rounded-lg">
                      <div className="text-xl font-bold text-blue-600">{meetingsStats.in_progress}</div>
                      <div className="text-xs text-muted-foreground">In Progress</div>
                    </div>
                    <div className="text-center p-2 bg-muted rounded-lg">
                      <div className="text-xl font-bold">{meetingsStats.scheduled}</div>
                      <div className="text-xs text-muted-foreground">Scheduled</div>
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Avg Meetings/Week</span>
                      <span className="font-bold">{meetingsStats.avg_meetings_per_week}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* To-Do Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ListChecks className="h-5 w-5 text-cyan-600" />
                  To-Do Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Total</span>
                    <span className="font-bold">{todosStats.total}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Completed</span>
                    <span className="font-bold text-green-600">{todosStats.completed}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Overdue</span>
                    <span className="font-bold text-red-600">{todosStats.overdue}</span>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground">Completion Rate</span>
                      <span className={`font-bold ${getHealthColor(todosStats.completion_rate)}`}>
                        {todosStats.completion_rate}%
                      </span>
                    </div>
                    <Progress value={todosStats.completion_rate} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Meetings List */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Meetings</CardTitle>
            <CardDescription>Last 20 meetings across all teams</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {meetings.slice(0, 10).map(meeting => (
                <div 
                  key={meeting.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/eos/l10-meetings/${meeting.id}`)}
                >
                  <div className="flex items-center gap-3">
                    {meeting.facilitator_display && (
                      <UserAvatar user={meeting.facilitator_display} size="sm" />
                    )}
                    <div>
                      <div className="font-medium">{meeting.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(meeting.scheduled_date).toLocaleDateString()}
                        {meeting.meeting_number && ` • Meeting #${meeting.meeting_number}`}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {meeting.participants_display?.length > 0 && (
                      <UserAvatarGroup users={meeting.participants_display} max={4} size="xs" />
                    )}
                    <Badge variant={
                      meeting.status === 'complete' ? 'default' :
                      meeting.status === 'in_progress' ? 'secondary' : 'outline'
                    }>
                      {meeting.status === 'complete' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {meeting.status === 'in_progress' && <Clock className="h-3 w-3 mr-1" />}
                      {meeting.status}
                    </Badge>
                  </div>
                </div>
              ))}
              
              {meetings.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No meetings found
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EOSLeadershipDashboard;
